import { inject, Injectable, Signal } from '@angular/core';
import {
  FormFieldOptions,
  FormFieldsLabelsConfig,
  FormFieldLabels,
  FormFieldLabelsConfig,
} from '../../form-field.types';
import {
  BehaviorSubject,
  combineLatestWith,
  filter,
  map,
  Observable,
  of,
  ReplaySubject,
  startWith,
  switchMap,
} from 'rxjs';
import { AbstractControl, FormGroup } from '@angular/forms';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { LangService } from 'src/app/shared/services/lang/lang.service';
import { Lang } from 'src/app/shared/services/lang/lang.types';

@Injectable()
export class FormFieldService {
  private configSubj = new BehaviorSubject<{
    labels: Record<string, FormFieldLabels>;
    options: Record<string, FormFieldOptions>;
  }>({ labels: {}, options: {} });
  private formSubj = new ReplaySubject<FormGroup>(1);
  private lang = inject(LangService).lang; // Could listen to the observable as well
  formGroup$ = this.formSubj.asObservable();
  formGroup = toSignal(this.formGroup$);

  setForm(formGroup: FormGroup) {
    this.formSubj.next(formGroup);
  }

  configure(config: {
    labels: FormFieldsLabelsConfig;
    options?: Record<string, FormFieldOptions>;
  }) {
    this.configSubj.next({
      labels: this.labelsConfigValues(config.labels),
      options: config.options ?? {},
    });
  }

  updateControlOptions(name: string, options: FormFieldOptions) {
    const oldConfig = this.configSubj.value;
    const oldOptions = oldConfig.options;
    const newOptions = { ...oldOptions, [name]: options };
    const newConfig = { ...oldConfig, options: newOptions };
    this.configSubj.next(newConfig);
  }

  updateControlLabels(
    name: string,
    labelsUpdate:
      | Partial<FormFieldLabelsConfig>
      | ((lang: Lang) => Partial<FormFieldLabelsConfig>),
  ) {
    const oldConfig = this.configSubj.value;
    const oldLabels = oldConfig.labels;
    const oldControlLabels = oldLabels[name] ?? {};
    const oldControlErrors = oldControlLabels.errors ?? {};
    const labelsUpdateValues = this.labelsValues(labelsUpdate);
    const errorsUpdate = labelsUpdateValues.errors ?? {};
    const newControlErrors = { ...oldControlErrors, ...errorsUpdate };
    const newControlLabels = {
      ...oldControlLabels,
      ...labelsUpdateValues,
      errors: newControlErrors,
    };
    const newLabels = { ...oldLabels, [name]: newControlLabels };
    const newConfig = { ...oldConfig, labels: newLabels };
    this.configSubj.next(newConfig);
  }

  formFieldConfig(name$: Observable<string>): Observable<{
    labels: FormFieldLabels;
    options: FormFieldOptions;
  }> {
    return name$.pipe(
      combineLatestWith(this.configSubj),
      map(([name, { labels, options }]) => {
        const { errors: customErrors, ...restCustomLabels } = this.labelsValues(
          labels[name],
        );
        const { errors: defaultErrors, ...restDefaultLabels } =
          this.labelsValues(options[name]?.defaultLabels);
        const errors = { ...defaultErrors, ...customErrors };
        const restLabels = { ...restDefaultLabels, ...restCustomLabels };
        const controlLabels = { errors, ...restLabels };
        if (!(name in options)) {
          // Text field by default
          return {
            labels: controlLabels,
            options: FormFieldOptions.text(),
          };
        }
        return {
          options: options[name],
          labels: controlLabels,
        };
      }),
    );
  }

  control(
    name: Signal<string | null | undefined>,
    // TODO: implement path support for all supported form fields and remove `?`.
    path?: Signal<string | (string | number)[] | undefined>,
  ): Signal<AbstractControl | undefined> {
    const name$ = toObservable(name);
    const path$ = path ? toObservable(path) : of('');
    const control$ = name$.pipe(
      switchMap(name => (name ? of(name) : of())),
      combineLatestWith(this.formSubj, path$),
      switchMap(([name, form, path]) => {
        const control = form?.get(path ?? name);
        return control ? of(control) : of();
      }),
    );
    return toSignal(control$);
  }

  controlErrorMessage(
    name: Signal<string | null | undefined>,
    // TODO: implement path support for all supported form fields and remove `?`.
    path?: Signal<string | null | undefined>,
  ): Signal<string | undefined> {
    const name$ = toObservable(name);
    const path$ = path ? toObservable(path) : of('');
    const errorMessage$ = name$.pipe(
      switchMap(name => (name ? of(name) : of())),
      combineLatestWith(this.configSubj, this.formSubj, path$),
      switchMap(([name, { labels, options }, form, path]) => {
        const control = form?.get(path ?? name);
        return control ? of({ form, name, control, labels, options }) : of();
      }),
      switchMap(({ form, name, control, labels, options }) =>
        form.statusChanges.pipe(
          startWith('INVALID'),
          filter(status => status === 'INVALID'),
          map(() => {
            const customControlErrorMessages =
              this.labelsValues(labels[name]).errors ?? {};
            const defaultControlErrorMessages =
              this.labelsValues(options[name]?.defaultLabels).errors ?? {};
            const controlErrorMessages = {
              ...defaultControlErrorMessages,
              ...customControlErrorMessages,
            };
            // Compute the error message for the control using the following
            // algorithm.
            // 1. If the control has errors, pick the message associated with
            // one of the errors and return.  (It is a configuration mistake if
            // no error has the associated message.)
            // 2. If the control has no errors, pick the message associated
            // with one of the form's errors.  This is used in
            // cross-validation.
            const formErrors = form.errors;
            const controlErrors = control.errors;
            const errors = controlErrors ? controlErrors : (formErrors ?? {});
            for (const [errorName, errorObject] of Object.entries(errors)) {
              const errorMessage =
                controlErrorMessages[errorName]?.(errorObject);
              if (errorMessage) {
                return errorMessage;
              }
            }
            return '';
          }),
        ),
      ),
    );
    return toSignal(errorMessage$);
  }

  private normalizeOptions(
    options?: ({ value: string; label?: string } | string)[],
  ): { value: string; label: string }[] | undefined {
    if (options === undefined) {
      return undefined;
    }
    return options.map(option => {
      if (typeof option === 'string') {
        return { value: option, label: option };
      }
      return { value: option.value, label: option.label ?? option.value };
    });
  }

  // `any` is used in this method because ValidationErrors is defined as  type
  // ValidationErrors = {  [key: string]: any; }
  private normalizeErrors(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    errors?: Record<string, string | ((error: any) => string)>,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ): Record<string, (error: any) => string> {
    if (!errors) {
      return {};
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const normalizedErrors: Record<string, (error: any) => string> = {};
    Object.entries(errors).forEach(([errorName, errorMessage]) => {
      const errorMessageFn =
        typeof errorMessage === 'function' ? errorMessage : () => errorMessage;
      normalizedErrors[errorName] = errorMessageFn;
    });
    return normalizedErrors;
  }

  private labelsValues(
    labels:
      | undefined
      | Partial<FormFieldLabelsConfig>
      | ((lang: Lang) => Partial<FormFieldLabelsConfig>),
  ): Partial<FormFieldLabels> {
    if (labels === undefined) {
      return {};
    }
    if (typeof labels === 'object') {
      return {
        ...labels,
        options: this.normalizeOptions(labels.options),
        errors: this.normalizeErrors(labels.errors),
      };
    }
    return this.labelsValues(labels(this.lang));
  }

  private labelsConfigValues(
    labelsConfig: FormFieldsLabelsConfig,
  ): Record<string, FormFieldLabels> {
    const configValue: Record<string, FormFieldLabels> = {};
    Object.entries(labelsConfig).forEach(([name, labels]) => {
      const labelsValues =
        typeof labels === 'function' ? labels(this.lang) : labels;
      const options = this.normalizeOptions(labelsValues.options);
      const errors = this.normalizeErrors(labelsValues.errors);
      configValue[name] = { ...labelsValues, options, errors };
    });
    return configValue;
  }
}
